import {computed, Injectable, signal} from '@angular/core'
import {List} from 'immutable'
import {FavoriteDto, FavoriteKind} from 'shared/favorites'
import {
    AddFavoriteCommandNamespace,
    GetFavoritesCommandNamespace,
    RemoveFavoriteCommandNamespace
} from 'shared/commands/favorites'
import {RemoteCommandService} from './remote.command.service'
import {EffectsService, SnackBarMessageEffect} from './effects.service'
import {Errors, getMessageIdByError} from 'shared/errors'
import {TranslateService} from '@ngx-translate/core'
import {MatDialog} from '@angular/material/dialog'
import {AlertDialog} from '../ui/chat-module/components/alert-dialog/alert-dialog.component'
import {ClientUser} from 'shared/user'
import {isPremiumActive} from '../utils/clientutils'

@Injectable({
    providedIn: 'root'
})
export class FavoritesService {
    readonly favorites = signal<List<FavoriteDto>>(List())
    readonly hiddenGuestCount = signal<number>(0)

    /**
     * Производный набор логинов избранных в нижнем регистре для быстрой O(1) проверки.
     * Используется в горячем пути сортировки списка пользователей (см. isFavorite),
     * где линейный .some() по List давал O(n) на каждое сравнение.
     */
    private readonly favoriteLogins = computed<Set<string>>(() => {
        const set = new Set<string>()
        this.favorites().forEach(f => set.add(f.login.toLowerCase()))
        return set
    })

    constructor(
        private remoteCommandService: RemoteCommandService,
        private effectsService: EffectsService,
        private translate: TranslateService,
        private dialog: MatDialog
    ) {
    }

    async loadFavorites(): Promise<void> {
        const response = await this.remoteCommandService.sendCommandWithCallback<GetFavoritesCommandNamespace.Response>(
            new GetFavoritesCommandNamespace.Request()
        )
        if (response.success) {
            this.favorites.set(List(response.favorites))
            this.hiddenGuestCount.set(response.hiddenGuestCount)
        }
    }

    addFavoriteWithValidation(user: ClientUser, me: ClientUser | null, isUpgrade: boolean = false): void {
        if (!me || !me.registered) {
            this.showError(Errors.FAVORITES_ONLY_FOR_REGISTERED)
            return
        }

        if (!user.registered && !isUpgrade) {
            if (!isPremiumActive(me)) {
                this.showError(Errors.FAVORITES_GUEST_REQUIRES_PREMIUM)
                return
            }

            const dialogRef = this.dialog.open(AlertDialog, {
                width: '70%',
                data: {
                    title: this.translate.instant('add_favorite_guest_warning_title'),
                    body: this.translate.instant('add_favorite_guest_warning_body'),
                    successButton: this.translate.instant('understand_and_add'),
                    cancelButton: this.translate.instant('cancel'),
                }
            })
            dialogRef.afterClosed().subscribe(result => {
                if (result && result.success) {
                    this.addFavorite(user.login).then()
                }
            })
        } else {
            this.addFavorite(user.login).then()
        }
    }

    public async addFavorite(login: string): Promise<void> {
        const lowerLogin = login.toLowerCase();
        // Оптимистичное обновление
        const tempFavorite: FavoriteDto = {
            login,
            kind: FavoriteKind.Registered, // Временно, сервер уточнит
            addedAt: Date.now()
        }
        
        const oldFavorites = this.favorites()
        this.favorites.update(list => {
            const index = list.findIndex(f => f.login.toLowerCase() === lowerLogin)
            if (index !== -1) {
                return list.set(index, tempFavorite)
            }
            return list.push(tempFavorite)
        })

        try {
            const response = await this.remoteCommandService.sendCommandWithCallback<AddFavoriteCommandNamespace.Response>(
                new AddFavoriteCommandNamespace.Request(login)
            )
            if (response.success && response.favorite) {
                // Заменяем на реальный объект от сервера
                this.favorites.update(list => {
                    const index = list.findIndex(f => f.login.toLowerCase() === lowerLogin)
                    if (index !== -1) {
                        return list.set(index, response.favorite!)
                    }
                    return list
                })
            } else {
                this.favorites.set(oldFavorites)
                this.showError(response.error)
            }
        } catch (e) {
            this.favorites.set(oldFavorites)
        }
    }

    async removeFavorite(login: string): Promise<void> {
        const lowerLogin = login.toLowerCase();
        const oldFavorites = this.favorites()
        this.favorites.update(list => list.filter(f => f.login.toLowerCase() !== lowerLogin))

        try {
            const response = await this.remoteCommandService.sendCommandWithCallback<RemoveFavoriteCommandNamespace.Response>(
                new RemoveFavoriteCommandNamespace.Request(login)
            )
            if (!response.success) {
                this.favorites.set(oldFavorites)
                this.showError(response.error)
            }
        } catch (e) {
            this.favorites.set(oldFavorites)
        }
    }

    public showError(error: number | undefined): void {
        if (!error) return
        
        const messageId = getMessageIdByError(error)
        const translatedMessage = this.translate.instant(messageId)
        this.effectsService.broadcast(new SnackBarMessageEffect(translatedMessage, '', 5000))
    }

    isFavorite(login: string): boolean {
        return this.favoriteLogins().has(login.toLowerCase())
    }

    getFavorite(login: string): FavoriteDto | undefined {
        const lowerLogin = login.toLowerCase();
        return this.favorites().find(f => f.login.toLowerCase() === lowerLogin)
    }

    /**
     * Обновляет отображаемый регистр логина гостевого избранного на актуальный.
     *
     * Источник истины для регистра ника анонимного пользователя — его последний вход в чат
     * (см. docs/specs/favorites.md). Сервер присылает актуальный регистр только при загрузке
     * списка избранного (GetFavorites). Если гость, находящийся у нас в избранном, заходит
     * под другим регистром во время сессии, нужно обновить сохранённый логин, иначе после его
     * выхода в списке пользователей отобразится устаревший регистр (например, добавили "Ааааа",
     * гость зашёл как "АаааА" — после выхода без этого обновления снова показывался бы "Ааааа").
     *
     * Применяется только к гостевым записям: для зарегистрированных регистр логина стабилен.
     */
    updateGuestFavoriteLogin(login: string): void {
        const lowerLogin = login.toLowerCase()
        const current = this.favorites().find(f => f.login.toLowerCase() === lowerLogin)
        if (!current || current.kind !== FavoriteKind.Guest || current.login === login) {
            return
        }
        this.favorites.update(list => {
            const index = list.findIndex(f => f.login.toLowerCase() === lowerLogin)
            if (index !== -1) {
                return list.set(index, {...list.get(index)!, login})
            }
            return list
        })
    }
}
